/**
Copyright (C) 2022 by SeQuenX  - All Rights Reserved
This file is part of the ComplyVantage product development,
and is released under the "Commercial License Agreement".
You should have received a copy of the Commercial License Agreement license with
this file. If not, please write to: legal@sequenx.com, or visit www.sequenx.com
*/
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const RemoteTeamsDetails = () => {
  const { t } = useTranslation();

  const services = [
    {
      title: t("remoteTeams.services.core.title"),
      description: t("remoteTeams.services.core.description"),
      features: t("remoteTeams.services.core.features", {
        returnObjects: true,
      }),
      color: "from-slate-700 to-slate-800",
    },
    {
      title: t("remoteTeams.services.frontend.title"),
      description: t("remoteTeams.services.frontend.description"),
      features: t("remoteTeams.services.frontend.features", {
        returnObjects: true,
      }),
      color: "from-slate-600 to-slate-700",
    },
    {
      title: t("remoteTeams.services.backend.title"),
      description: t("remoteTeams.services.backend.description"),
      features: t("remoteTeams.services.backend.features", {
        returnObjects: true,
      }),
      color: "from-slate-800 to-slate-900",
    },
    {
      title: t("remoteTeams.services.devops.title"),
      description: t("remoteTeams.services.devops.description"),
      features: t("remoteTeams.services.devops.features", {
        returnObjects: true,
      }),
      color: "from-slate-700 to-slate-800",
    },
    {
      title: t("remoteTeams.services.uxui.title"),
      description: t("remoteTeams.services.uxui.description"),
      features: t("remoteTeams.services.uxui.features", {
        returnObjects: true,
      }),
      color: "from-slate-600 to-slate-700",
    },
    {
      title: t("remoteTeams.services.qa.title"),
      description: t("remoteTeams.services.qa.description"),
      features: t("remoteTeams.services.qa.features", {
        returnObjects: true,
      }),
      color: "from-slate-800 to-slate-900",
    },
  ];

  const benefits = [
    {
      title: t("remoteTeams.benefits.cost.title"),
      description: t("remoteTeams.benefits.cost.description"),
      color: "from-slate-700 to-slate-800",
    },
    {
      title: t("remoteTeams.benefits.overhead.title"),
      description: t("remoteTeams.benefits.overhead.description"),
      color: "from-slate-600 to-slate-700",
    },
    {
      title: t("remoteTeams.benefits.talent.title"),
      description: t("remoteTeams.benefits.talent.description"),
      color: "from-slate-800 to-slate-900",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t("remoteTeams.title")}
          </h1>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            {t("remoteTeams.hero.subtitle")}
          </p>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-4">
            {t("remoteTeams.hero.description")}
          </p>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-4">
            {t("remoteTeams.hero.subDescription")}
          </p>
        </motion.div>

        {/* Services Section */}
        <div className="py-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">
            {t("remoteTeams.services.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`bg-gradient-to-br ${service.color} rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300`}
              >
                <div className="text-3xl mb-3">
                  {index === 0
                    ? "⚙️"
                    : index === 1
                    ? "🎨"
                    : index === 2
                    ? "🔧"
                    : index === 3
                    ? "🚀"
                    : index === 4
                    ? "🎯"
                    : "🔍"}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-white mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-white">
                      <svg
                        className="w-4 h-4 mr-2 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="py-16 bg-gray-50 rounded-lg">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {t("remoteTeams.benefits.title")}
            </h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-gray-700 max-w-2xl mx-auto">
              {t("remoteTeams.benefits.description")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 px-4 mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`bg-gradient-to-br ${benefit.color} rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300`}
              >
                <h3 className="text-xl font-semibold text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="text-white">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="container mx-auto py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-lg p-8 text-center"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              {t("remoteTeams.cta.title")}
            </h2>
            <p className="text-gray-200 mb-6 max-w-2xl mx-auto">
              {t("remoteTeams.cta.description")}
            </p>
            <p className="text-gray-200 mb-6 max-w-2xl mx-auto">
              {t("remoteTeams.cta.subDescription")}
            </p>
            <motion.a
              href="/contact-us"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-slate-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300"
            >
              {t("remoteTeams.cta.button")}
            </motion.a>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RemoteTeamsDetails;
